import type { RoomConfig } from './config';
import { deepFreeze } from './immutable';
import type { CardTitle, CardUid, PlayerId } from './ids';
import type {
  Decision,
  GamePhase,
  GameState,
  KnownCard,
  KnownInDraw,
  KnownInHand,
  PlayKind,
  TurnSlot,
} from './state';

/**
 * **视图裁剪** —— 决定「每个玩家能看到什么」的唯一地方。
 *
 * ## 为什么需要它
 *
 * 客户端**不跑 reducer、也不持有完整状态**。房主每步之后对每个玩家算一次
 * `project(state, viewer)`，只把结果发出去。
 *
 * 这不是为了防作弊（本项目不做安全设计），而是为了**同步正确性**：
 * 「哪些该显示」要有**一处**定义，而不是散落在每个客户端各自判断 ——
 * 否则迟早会出现「某台机器上对手的牌露出来了」这种 bug
 * （`architecture.md` §2.2）。
 *
 * ## 硬规矩：不在视图里的东西，客户端就永远拿不到
 *
 * | 绝对不能出现 | 为什么 |
 * | --- | --- |
 * | `state.cards`（uid → 牌名 的全表） | 有它就等于知道**所有**隐藏牌是什么 |
 * | `state.rng`（随机数状态） | 知道种子就能预测整副牌序 |
 * | 别人的 `hand` | 连 uid 都不发，只发数量 |
 * | `drawPile` 的内容 | 只发数量 |
 * | 别人的 `knowledge` | 「我知道什么」是私有信息 |
 * | `nopeEligible()` 的结果 | **谁手上有休想**是隐藏信息，绝不能顺手发出去 |
 *
 * 最后一条容易漏：房主要用它决定窗口等多久，但那是**房主本地**的事，
 * 不该进视图。
 */

// ============================================================
// 视图的形状
// ============================================================

/** 一张**看得见**的牌。看不见的牌不会以任何形式出现在视图里 */
export interface VisibleCard {
  readonly uid: CardUid;
  readonly title: CardTitle;
}

/** 某个玩家的**公开**信息 */
export interface PlayerViewPlayer {
  readonly id: PlayerId;
  readonly name: string;
  readonly alive: boolean;
  readonly connected: boolean;
  /** 手牌**数量**。具体是什么牌不发，连 uid 都不发 */
  readonly handCount: number;
}

/** 正在等某人做抉择 —— 只有公开的那部分 */
export interface PendingDecisionView {
  readonly player: PlayerId;
  readonly kind: Decision['kind'];
  /**
   * 只有 `kind === 'stealCard'` 时非空：**从谁手里抽、有几张牌背**。
   *
   * ⚠️ 这两条都是**公开信息**：被抽的人是谁，窗口里已经有了；
   * 他有几张牌，`PlayerViewPlayer.handCount` 本来就是公开的。
   * 抽牌人需要张数才能铺出那一扇牌背 —— 而**顺序不在这里**，
   * 顺序是本人的事（见 `net/side.ts`）。
   */
  readonly steal: { readonly from: PlayerId; readonly count: number } | null;
  /**
   * 如果这次抉择涉及一张**已经公开亮出**的牌（抽到致命牌时必须亮），
   * 这里给出它的牌名。
   *
   * ⚠️ 只给牌名，**不给 uid** —— 那张牌此刻在某人的手里，
   * 它的位置仍然是隐藏信息。
   */
  readonly revealedTitle: CardTitle | null;
  /** 三张同名索取指名要的那张牌（只有 giveCard 时非空）—— 公开信息 */
  readonly namedTitle?: CardTitle | null;
}

/** 响应窗口。休想链上的牌都在弃牌堆里，所以是公开的 */
export interface ResponseWindowView {
  readonly play: {
    readonly player: PlayerId;
    readonly cards: readonly VisibleCard[];
    readonly kind: PlayKind;
    readonly target: PlayerId | null;
  };
  readonly nopeChain: readonly { readonly player: PlayerId; readonly card: VisibleCard }[];
  /** 立刻关窗的话效果会不会被取消（链长奇偶）。纯粹是省得客户端自己算 */
  readonly cancelledIfClosedNow: boolean;
}

export interface PlayerView {
  /** 这个视图是给谁看的 */
  readonly you: PlayerId;

  readonly phase: GamePhase;
  readonly host: PlayerId;
  readonly config: RoomConfig;
  readonly winner: PlayerId | null;

  readonly players: readonly PlayerViewPlayer[];

  readonly drawPileCount: number;
  /** 弃牌堆是公开的，连 uid 一起发（动画要用） */
  readonly discardPile: readonly VisibleCard[];

  readonly turn: TurnSlot | null;
  readonly resolution: ResponseWindowView | null;

  /** 有人在等抉择 —— 公开到「谁、什么类型」为止 */
  readonly pending: PendingDecisionView | null;

  // ---------------- 以下只属于本人 ----------------

  /** **你自己的**手牌，完整的 */
  readonly yourHand: readonly VisibleCard[];

  /** **你自己的**待抉择内容（含候选列表这类细节）。不是你的就是 null */
  readonly yourDecision: Decision | null;

  /** **你自己**私下知道的牌堆顶（Debug 看来的） */
  readonly knownTop: readonly KnownCard[];
  /** 你确定在抽牌堆里、但不知道位次的期望张数（洗牌之后的产物） */
  readonly knownInDraw: readonly KnownInDraw[];
  /** 你确定在某位玩家手里的期望张数（看着他抽走你已知的牌） */
  readonly knownInHand: readonly KnownInHand[];
}

// ============================================================
// 裁剪
// ============================================================

function visible(state: GameState, uid: CardUid): VisibleCard | null {
  const title = state.cards[uid]?.title;
  return title === undefined ? null : { uid, title };
}

function visibleList(state: GameState, uids: readonly CardUid[]): VisibleCard[] {
  const out: VisibleCard[] = [];
  for (const uid of uids) {
    const card = visible(state, uid);
    if (card !== null) out.push(card);
  }
  return out;
}

/**
 * 算出一个玩家能看到的视图。
 *
 * **纯函数**，不读时钟、不用随机、不碰网络。给定同一个 `state` 和 `viewer`
 * 必然得到同一个结果，所以它也可以被快照、被测试、被哈希。
 */
export function project(state: GameState, viewer: PlayerId): PlayerView {
  const me = state.players.find((p) => p.id === viewer);

  const players: PlayerViewPlayer[] = state.players.map((p) => ({
    id: p.id,
    name: p.name,
    alive: p.alive,
    connected: p.connected,
    handCount: p.hand.length,
  }));

  const resolution: ResponseWindowView | null =
    state.resolution === null
      ? null
      : {
          play: {
            player: state.resolution.play.player,
            cards: visibleList(state, state.resolution.play.cards),
            kind: state.resolution.play.kind,
            target: state.resolution.play.target,
          },
          nopeChain: state.resolution.nopeChain.flatMap((entry) => {
            const card = visible(state, entry.card);
            return card === null ? [] : [{ player: entry.player, card }];
          }),
          cancelledIfClosedNow: state.resolution.nopeChain.length % 2 === 1,
        };

  // 公开的「有人在等抉择」—— 只有当事人拿到完整内容
  const decision = state.decision;
  const pending: PendingDecisionView | null =
    decision === null
      ? null
      : {
          player: decision.player,
          kind: decision.kind,
          revealedTitle:
            decision.kind === 'useRescue' || decision.kind === 'insertLethal'
              ? (state.cards[decision.card]?.title ?? null)
              : null,
          namedTitle: decision.kind === 'giveCard' ? decision.namedTitle : null,
          steal:
            decision.kind === 'stealCard'
              ? { from: decision.from, count: decision.count }
              : null,
        };

  // ⚠️ 视图也冻结。理由：视图里有几处**直接引用 state 内部对象**
  //（例如 `yourDecision`），只要两边都冻着，别名就是安全的；
  // 而客户端那边拿到的是序列化后的副本，本来也改不回来。
  return deepFreeze({
    you: viewer,
    phase: state.phase,
    host: state.host,
    config: state.config,
    winner: state.winner,
    players,
    drawPileCount: state.drawPile.length,
    discardPile: visibleList(state, state.discardPile),
    turn: state.turn === null ? null : { ...state.turn },
    resolution,
    pending,
    yourHand: visibleList(state, me?.hand ?? []),
    yourDecision: decision !== null && decision.player === viewer ? decision : null,
    knownTop: state.knowledge[viewer]?.knownTop ?? [],
    knownInDraw: state.knowledge[viewer]?.knownInDraw ?? [],
    knownInHand: state.knowledge[viewer]?.knownInHand ?? [],
  });
}

/** 一次性给所有玩家算视图。房主每步之后调用 */
export function projectAll(state: GameState): ReadonlyMap<PlayerId, PlayerView> {
  const out = new Map<PlayerId, PlayerView>();
  for (const player of state.players) out.set(player.id, project(state, player.id));
  return out;
}
